// docs/pos-cashier-operations-plan.md PR 5 — offline-resilient checkout.
// A localStorage-backed queue for sales that couldn't reach the server at
// submit time, plus the shared "replay the checkout sequence" logic used
// by both CheckoutPage's live completeSale() and the background sync
// manager (Components/PendingSalesSyncManager) mounted in App.tsx — one
// source of truth for "what does submitting a sale actually POST," so a
// queued sale replays identically to a live one.
import { authenticatedFetch } from "./api";
import { API_BASE } from "../config/api";
import { parseApiError } from "./parseApiError";

const STORAGE_KEY = "pendingSales";

export interface PendingSaleItem {
  product_id: number;
  product_name: string;
  product_image: string;
  unit_price: number;
  quantity: number;
}

// Everything submitPendingSale actually needs to replay a sale — deliberately
// separate from PendingSale (below) so completeSale's live path can call
// submitPendingSale directly without fabricating a meaningless id/queued_at
// for a sale that was never queued.
export interface SaleSubmission {
  order_type: "pickup" | "delivery";
  total_amount: number;
  discount_amount: number;
  created_by: number | null;
  items: PendingSaleItem[];
  payment_method: "cash" | "card" | "online";
  amount: number;
  amount_tendered: number | null;
  change_given: number | null;
}

export interface PendingSale extends SaleSubmission {
  id: string;
  queued_at: string;
  // Set once a retry comes back with a real (non-network) rejection — left
  // in the queue for manual review rather than retried forever.
  error?: string;
}

export const loadPendingSales = (): PendingSale[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const savePendingSales = (sales: PendingSale[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sales));
  // Same-tab listeners (CheckoutPage's pending-count badge) don't get
  // the browser's cross-tab "storage" event, since that only fires for
  // *other* tabs/windows — this is the in-tab equivalent.
  window.dispatchEvent(new Event("pendingSalesChanged"));
};

export const enqueuePendingSale = (sale: SaleSubmission): PendingSale => {
  const withId: PendingSale = {
    ...sale,
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    queued_at: new Date().toISOString(),
  };
  savePendingSales([...loadPendingSales(), withId]);
  return withId;
};

export const removePendingSale = (id: string) => {
  savePendingSales(loadPendingSales().filter((s) => s.id !== id));
};

const markPendingSaleError = (id: string, message: string) => {
  savePendingSales(loadPendingSales().map((s) => (s.id === id ? { ...s, error: message } : s)));
};

// A rejected fetch() (TypeError) means the request never reached the
// server at all — offline, DNS failure, connection dropped mid-request.
// Distinct from an HTTP error response (a real 4xx/5xx), which means the
// server *did* respond and rejected the sale for a real reason — that
// should never be silently retried forever.
const isNetworkError = (err: any) => err instanceof TypeError;

export interface SubmitResult {
  ok: boolean;
  orderId?: number;
  networkError?: boolean;
  message?: string;
}

// The single shared implementation of "submit a sale" (POST /order ->
// /orderitem xN -> /orderpayment -> /orderstatus) — CheckoutPage.completeSale
// calls this directly for a live submission, and the background sync
// manager calls it (with the same SaleSubmission shape, sourced from a
// queued PendingSale) to replay a queued one. There's exactly one
// implementation of this sequence, not two that could drift apart.
export const submitPendingSale = async (sale: SaleSubmission, auth: any): Promise<SubmitResult> => {
  try {
    const orderRes = await authenticatedFetch(
      `${API_BASE}/order`,
      {
        method: "POST",
        body: JSON.stringify({
          order_type: sale.order_type,
          total_amount: sale.total_amount,
          discount_amount: sale.discount_amount,
          delivery_date: new Date().toISOString(),
          created_by: sale.created_by,
          discount_id: null,
        }),
      },
      auth
    );
    if (!orderRes.ok) {
      return { ok: false, message: await parseApiError(orderRes, "Failed to create order") };
    }
    const { id: orderId } = await orderRes.json();

    for (const item of sale.items) {
      const itemRes = await authenticatedFetch(
        `${API_BASE}/orderitem`,
        {
          method: "POST",
          body: JSON.stringify({
            order_id: orderId,
            product_id: item.product_id,
            quantity: item.quantity,
            unit_price: item.unit_price,
            total_price: parseFloat((item.unit_price * item.quantity).toFixed(2)),
            product_name: item.product_name,
            product_image: item.product_image,
          }),
        },
        auth
      );
      if (!itemRes.ok) {
        return { ok: false, orderId, message: await parseApiError(itemRes, "Failed to add a sold item to the order") };
      }
    }

    const paymentRes = await authenticatedFetch(
      `${API_BASE}/orderpayment`,
      {
        method: "POST",
        body: JSON.stringify({
          order_id: orderId,
          payment_method: sale.payment_method,
          payment_status: "paid",
          amount: sale.amount,
          amount_tendered: sale.amount_tendered,
          change_given: sale.change_given,
        }),
      },
      auth
    );
    if (!paymentRes.ok) {
      return { ok: false, orderId, message: await parseApiError(paymentRes, "Failed to record payment") };
    }

    const statusRes = await authenticatedFetch(
      `${API_BASE}/orderstatus`,
      {
        method: "POST",
        body: JSON.stringify({ order_id: orderId, status_name: sale.order_type === "pickup" ? "delivered" : "confirming" }),
      },
      auth
    );
    if (!statusRes.ok) {
      return { ok: false, orderId, message: await parseApiError(statusRes, "Failed to set the order status") };
    }

    return { ok: true, orderId };
  } catch (err: any) {
    if (isNetworkError(err)) {
      return { ok: false, networkError: true, message: "Network unavailable" };
    }
    return { ok: false, message: err.message || "Unexpected error" };
  }
};

// Drains every still-queued sale in order. A network failure stops the
// drain outright (nothing else will succeed either, no point burning
// through the rest). A real rejection flags just that one sale with
// .error and moves on — it needs a human, not an infinite retry loop.
export const drainPendingSales = async (
  auth: any,
  onSynced?: (sale: PendingSale, orderId: number) => void
): Promise<void> => {
  const sales = loadPendingSales();
  for (const sale of sales) {
    if (sale.error) continue;
    const result = await submitPendingSale(sale, auth);
    if (result.ok && result.orderId) {
      removePendingSale(sale.id);
      onSynced?.(sale, result.orderId);
    } else if (result.networkError) {
      break;
    } else {
      markPendingSaleError(sale.id, result.message || "Failed to sync this sale");
    }
  }
};
